import React, { useCallback, useState, useEffect } from 'react'
import { PiggyBankForm, PiggyBank, Branch } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { useRedirectHandleBack, useDialogHandleSubmit, useGetDataByParams } from '../../../utils/default/ComplexFlowHook'
import { Path } from '../../../components/admin/route/route'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import EditFinancePiggyBankForm, { EditFinancePiggyBankFormType, initialForm } from '../../../components/admin/finance/EditFinancePiggyBankForm'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { formatMoney } from '../../../utils/default/TableHelper'

const formToRequest = (form: EditFinancePiggyBankFormType): Partial<PiggyBankForm> => ({
  cash_max: Number(form.max),
  cash_min: Number(form.min),
  real_name: form.receiver,
  memo: form.memo,
  group_id: form.group === '--' ? null : form.group,
  is_active: form.isActive,
  clients: form.clients,
  layer_ids: form.layer.layers,
  code: form.code,
  is_for_proxy_deposit: form.layer.isForProxyDeposit,
  is_recommended: form.isRecommended
})

const FinancePiggyBankUpdatePage: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const [id, setId] = useState<number>(0)
  const [bankName, setBankName] = useState<string>('')
  const [defaultForm, setDefaulForm] = useState<EditFinancePiggyBankFormType>(initialForm())
  const pageFlow = usePageFlow()
  const globalDialog = useGlobalDialog()
  useGetDataByParams({
    path: Path.FINANCE_PIGGY_BANK_UPDATE,
    onBeforeFetch: pageFlow.setLoadingStart,
    gdkFunc: (param: { id: string }) => gdk.finance.getPiggyBank(Number(param.id)),
    gdkFuncDependencies: [gdk],
    onSuccess: (res: PiggyBank<number>) => {
      setId(res.id)
      setBankName(res.name)
      setDefaulForm(initialForm({
        account: res.account,
        group: res.group.id ?? '--',
        min: formatMoney(res.cash_min),
        max: formatMoney(res.cash_max),
        receiver: res.real_name,
        memo: res.memo,
        isActive: res.is_active,
        isRecommended: res.is_recommended,
        clients: res.clients,
        layer: {
          layers: res.layers.map((item) => item.id),
          isForProxyDeposit: res.is_for_proxy_deposit
        }
      }))
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: id === 0
  })
  useEffect(() => {
    gdk.trunk.trigger([Branch.PLAYER_LAYER])
  }, [gdk])
  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({ path: Path.FINANCE_PIGGY_BANK })
  const { handleSubmit } = useDialogHandleSubmit({
    formToRequest,
    gdkFunc: (payload) => gdk.finance.updatePiggyBank(id, payload),
    gdkFuncDependencies: [gdk, id],
    dialogId: 'updatePiggyBank',
    globalDialog,
    getChangeDialogConfig: useCallback((form: EditFinancePiggyBankFormType) => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmUpdatePiggyBank')}
          <span className={classes.purpleWord}>{bankName} {form.receiver}</span>
        </React.Fragment>
      )
    }), [t, classes.purpleWord, bankName]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.updatePiggyBankSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    afterSuccessDialog: handleBack
  })
  return (
    <Box padding={5}>
      <Paper>
        <Box padding={4}>
          <Box
            paddingY={1.25}
            paddingX={2}
            className={classes.pinkTitleBar}
          >
            <Typography variant="h5">
              {t('page.updatePiggyBank')}
            </Typography>
          </Box>
          <Box paddingY={2}>
            <LoadingAndErrorFrame { ...pageFlow.status }>
              <EditFinancePiggyBankForm
                okText={t('common.confirmUpdate')}
                onBack={handleDebouncedBack}
                onSubmit={handleSubmit}
                defaultValue={defaultForm}
                banks={[]}
                disableBank
                disableAccount
                bankName={bankName}
              />
            </LoadingAndErrorFrame>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default React.memo(FinancePiggyBankUpdatePage)
